import React from 'react';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Typography, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    Badge,
    Box,
    Button,
    Menu,
    MenuItem,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { IoChatbubbleEllipsesOutline } from 'react-icons/io5';

const HelpMenu = ({
    anchorEl,
    handleClose,
    intercomNotifications,
    sxBadge,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const open = Boolean(anchorEl);

    const handleIntercomOpen = () => {
        if (window.Intercom) {
            window.Intercom('showSpace', 'home');
        }
        handleClose();
    };

    return (
        <Menu
            anchorEl={anchorEl}
            onClose={handleClose}
            open={open}
            anchorOrigin={{
                horizontal: 'right',
                vertical: 'bottom',
            }}
            transformOrigin={{
                horizontal: 'right',
                vertical: 'top',
            }}
            MenuListProps={{
                disablePadding: true,
            }}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.2)',
                    overflow: 'hidden',
                    width: 'auto',
                },
            }}
        >
            {/* Header Area */}
            <Box
                sx={{
                    px: 2.5,
                    pt: 2.5,
                    pb: 1,
                }}
            >
                <Typography variant="subtitle1" fontWeight="bold">
                    {t('How can we help you?')}
                </Typography>
            </Box>

            <Box sx={{ p: 1 }}>
                <MenuItem
                    component={Button}
                    href={`${import.meta.env.VITE_ACADEMY_URL}`}
                    onClick={handleClose}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        borderRadius: 2,
                        gap: 1.5,
                        p: 1.5,
                        '&:hover': {
                            backgroundColor: 'action.hover',
                        },
                    }}
                    variant="text"
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            bgcolor: 'rgba(45, 212, 246, 0.1)',
                            borderRadius: 2,
                            display: 'flex',
                            height: 40,
                            justifyContent: 'center',
                            width: 40,
                        }}
                    >
                        <MenuBookIcon
                            sx={{ color: 'primary.main', fontSize: 20 }}
                        />
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="text.primary"
                            sx={{ lineHeight: 1.2, mb: 0.5 }}
                        >
                            Alurack Academy
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('Tutorials, guides and resources')}
                        </Typography>
                    </Box>
                    <OpenInNewIcon
                        sx={{ color: 'primary.main', fontSize: 18 }}
                    />
                </MenuItem>

                <Divider sx={{ my: 0.5, mx: 2 }} />

                <MenuItem
                    onClick={handleIntercomOpen}
                    sx={{
                        borderRadius: 2,
                        gap: 1.5,
                        p: 1.5,
                        '&:hover': {
                            backgroundColor: 'action.hover',
                        },
                    }}
                >
                    <Box
                        sx={{
                            alignItems: 'center',
                            bgcolor: 'rgba(45, 212, 246, 0.1)',
                            borderRadius: 2,
                            display: 'flex',
                            height: 40,
                            justifyContent: 'center',
                            width: 40,
                        }}
                    >
                        <Badge
                            badgeContent={intercomNotifications}
                            sx={sxBadge}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    p: '4px',
                                }}
                            >
                                <IoChatbubbleEllipsesOutline
                                    size={20}
                                    style={{
                                        color: theme.palette.primary.main,
                                    }}
                                />
                            </Box>
                        </Badge>
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                        <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            color="text.primary"
                            sx={{ lineHeight: 1.2, mb: 0.5 }}
                        >
                            {t('Support team')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t('Talk to our support team')}
                        </Typography>
                    </Box>
                    <ChevronRightIcon
                        sx={{ color: 'primary.main', fontSize: 18 }}
                    />
                </MenuItem>
            </Box>
        </Menu>
    );
};

HelpMenu.propTypes = {
    anchorEl: PropTypes.object,
    handleClose: PropTypes.func.isRequired,
    intercomNotifications: PropTypes.number,
    sxBadge: PropTypes.object,
};

export default HelpMenu;
